import { mat4 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { Series } from '~/domain/dicom/series';
import { Image3D } from '~/domain/patient/image3d';
import { assembleVolumeFromSlices } from '~/domain/services/assembleVolume';
import {
    buildNeutralHFSGrid,
    resampleInt16Grid,
} from '~/domain/services/resampleGrid';
import { buildDerivedSeriesFromVolume } from '~/domain/services/dicomRewriteImage';
import { ResampleSize } from './reformatValidation';
import { getModalityBackgroundValue } from './modalityDefaults';

export type ReformatTarget =
    | { kind: 'rawSeries'; series: Series }
    | { kind: 'loadedImage'; image: Image3D };

export interface ReformatParams {
    target: ReformatTarget;
    sliceThicknessMm: number;
    resampleSize: ResampleSize;
    fovMm: number;
    zRange: [number, number];
    outputDescription: string;
}

export interface ReformatResult {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dicomDatasets: any[];
}

/**
 * Execute the reformat workflow: assemble/read source volume,
 * build destination grid, resample, and generate derived DICOM datasets.
 *
 * The caller handles persistence (DicomProcessor) and UI feedback.
 */
export async function executeReformat(
    params: ReformatParams
): Promise<ReformatResult> {
    const {
        target,
        sliceThicknessMm,
        resampleSize,
        fovMm,
        zRange,
        outputDescription,
    } = params;

    let srcPixels: Int16Array;
    let srcGrid: Grid;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let exampleDataset: any;

    if (target.kind === 'rawSeries') {
        const assembled = assembleVolumeFromSlices(target.series, zRange);
        srcPixels = assembled.pixels;
        srcGrid = assembled.grid;
        exampleDataset =
            target.series.dicomImageSlices[0].datasetWrapper.dataset;
    } else {
        srcPixels = target.image.pixelData;
        srcGrid = target.image.grid;
        exampleDataset = target.image.firstSlice.datasetWrapper.dataset;
    }

    // Reformat is MPR axial: always reslice into a neutral HFS grid
    // along this normal. For raw series the slices were already
    // cropped during assembly, so no zRange is forwarded.
    const zRangeMm: [number, number] | undefined =
        target.kind === 'loadedImage' ? zRange : undefined;
    const dstGrid = buildNeutralHFSGrid(
        srcGrid,
        resampleSize,
        resampleSize,
        sliceThicknessMm,
        fovMm,
        zRangeMm
    );

    const modality =
        target.kind === 'rawSeries'
            ? target.series.seriesModule.modality
            : target.image.seriesMetadata.modality;

    // Use a modality-appropriate background for samples that fall
    // outside the source volume rather than edge-clamping.
    const resampledImage = resampleInt16Grid(srcPixels, srcGrid, dstGrid, {
        patientSrcFromDst: mat4.create(),
        clampToEdge: false,
        backgroundValue: getModalityBackgroundValue(modality),
    });

    if (!resampledImage) {
        throw new Error('Resampling failed');
    }

    // Preserve the original Frame of Reference UID: the resampled
    // image lives in the same patient coordinate system even when
    // orientation changes (oblique → HFS), so existing structure
    // sets and registrations remain associated.
    const originalForUid: string | undefined =
        exampleDataset.FrameOfReferenceUID;

    // Build DICOM series
    const newDicoms = await buildDerivedSeriesFromVolume(
        resampledImage.pixels,
        resampledImage.dstGrid,
        exampleDataset,
        originalForUid
    );

    // Override series description if user provided one
    const desc = outputDescription.trim();
    if (desc) {
        for (const ds of newDicoms) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ds.SeriesDescription = desc;
        }
    }

    return { dicomDatasets: newDicoms };
}
